import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  MenuItem,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Switch,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ViewColumn,
  Info as InfoIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import SidebarPanelHeader from './SidebarPanelHeader';
import {
  useCampaignId,
  useSpreadsheetViewType
} from '../../context/CampaignContext';
import { useSnackbar } from '../../context/SnackbarContext';
import { renameColumnInCrmMappingIfPresent } from '../../api/crmMapping';
import {
  Column,
  displayColumnName,
  isLockedColumn,
  getAvailableDefinedSignals,
  updateColumn
} from '../../api/spreadsheet';
import {
  SPREADSHEET_COLUMN_TYPES,
  SPREADSHEET_COLUMN_TYPE_LABELS,
  type SpreadsheetColumnType
} from '../../constants/spreadsheetColumnTypes';
import type { ColumnEditorInfo } from '../spreadsheet/spreadsheetTypes';

export type { ColumnEditorInfo };

/** AI enrichment mode: use backend presets or custom free-text prompt. */
export type EnrichmentMode = 'presets' | 'freeText';

export type EnrichmentPreset = { id: string; label: string };

/** Updates passed to onColumnUpdated; previousColumnName is set when the column was renamed. */
export type ColumnUpdatedPayload = Partial<ColumnEditorInfo> & {
  previousColumnName?: string;
};

interface ColumnEditorPanelProps {
  column: ColumnEditorInfo;
  onClose: () => void;
  /** Called after a successful update so the parent can sync state (e.g. after name change). */
  onColumnUpdated?: (updates: ColumnUpdatedPayload) => void;
}

const LOCKED_COLUMN_TOOLTIP = 'This column cannot be edited.';

const COLUMN_NAME_EMPTY_MSG = 'Column name cannot be empty.';
const COLUMN_NAME_DOLLAR_PREFIX_MSG = 'Column names cannot start with $.';
const COLUMN_NAME_FORWARD_SLASH_MSG = 'Column names cannot contain /.';
const COLUMN_NAME_DUPLICATE_MSG =
  'A column with this name already exists. Choose a different name.';

function validateEditableColumnRename(
  trimmed: string,
  columnIndex: number,
  allColumnNames: string[]
): string | null {
  if (trimmed === '') return COLUMN_NAME_EMPTY_MSG;
  if (trimmed.startsWith('$')) return COLUMN_NAME_DOLLAR_PREFIX_MSG;
  if (trimmed.includes('/')) return COLUMN_NAME_FORWARD_SLASH_MSG;
  for (let j = 0; j < allColumnNames.length; j++) {
    if (j === columnIndex) continue;
    if (allColumnNames[j] === trimmed) return COLUMN_NAME_DUPLICATE_MSG;
  }
  return null;
}

const ColumnEditorPanel: React.FC<ColumnEditorPanelProps> = ({
  column,
  onClose,
  onColumnUpdated
}) => {
  const campaignId = useCampaignId();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const isLocked = isLockedColumn(column.columnName);
  const [name, setName] = useState(displayColumnName(column.columnName));
  const [description, setDescription] = useState(column.description ?? '');
  const [colType, setColType] = useState<string>(column.columnType ?? 'str');
  const [enrichmentMode, setEnrichmentMode] =
    useState<EnrichmentMode>('presets');
  const [enrichmentPresets, setEnrichmentPresets] = useState<
    EnrichmentPreset[]
  >([]);
  const [enrichmentPresetsLoading, setEnrichmentPresetsLoading] =
    useState(false);
  const [enrichmentPresetId, setEnrichmentPresetId] = useState<string>('');
  const [enrichmentFreeText, setEnrichmentFreeText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Free text draft kept in memory when user switches to presets; cleared when panel closes (unmount). */
  const freeTextDraftRef = useRef<string>('');

  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  /** AI-enabled when column has a definition object (read from column_data.definition). */
  const definitionValue = column.definition?.value;
  /** Local aiEnabled state; initialized from column, updated on toggle (persisted immediately). */
  const [aiEnabled, setAiEnabled] = useState(!!column.definition);

  useEffect(() => {
    setName(displayColumnName(column.columnName));
    setDescription(column.description ?? '');
    setColType(column.columnType ?? 'str');
    setAiEnabled(!!column.definition);
    setNameError(null);
  }, [
    column.columnName,
    column.description,
    column.columnType,
    column.definition
  ]);

  /** Fetch available defined signals (enrichment presets) from the API. */
  useEffect(() => {
    if (!campaignId) return;
    setEnrichmentPresetsLoading(true);
    getAvailableDefinedSignals(campaignId, viewType)
      .then((res) => {
        const presets: EnrichmentPreset[] = (res.signal_names ?? [])
          .map((signalName) => ({
            id: signalName,
            label: signalName
          }))
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
          );
        setEnrichmentPresets(presets);
        setEnrichmentPresetId((prev) => {
          const stillValid = presets.some((p) => p.id === prev);
          return stillValid ? prev : (presets[0]?.id ?? '');
        });
      })
      .catch(() => {
        showSnackbar('Failed to load enrichment presets', 'error');
      })
      .finally(() => {
        setEnrichmentPresetsLoading(false);
      });
  }, [campaignId, showSnackbar]);

  /** Sync UI from column definition. Only treat as preset when definition type is defined_signal;
   * otherwise user may have typed free text that happens to match a preset label. */
  const definitionType = column.definition?.column_definition_type;
  useEffect(() => {
    if (definitionValue == null || definitionValue === '') return;
    if (definitionType === 'defined_signal') {
      const matchingPreset = enrichmentPresets.find(
        (p) => p.label === definitionValue || p.id === definitionValue
      );
      if (matchingPreset) {
        setEnrichmentMode('presets');
        setEnrichmentPresetId(matchingPreset.id);
        setEnrichmentFreeText(''); // Don't keep preset text in free-text state; switching to free text should show empty
      } else {
        setEnrichmentMode('freeText');
        setEnrichmentFreeText(definitionValue);
      }
    } else {
      setEnrichmentMode('freeText');
      setEnrichmentFreeText(definitionValue);
    }
  }, [definitionValue, definitionType, enrichmentPresets]);

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleNameBlur = () => {
    if (isLocked) return;
    const trimmed = name.trim();
    const displayName = displayColumnName(column.columnName);
    const validationMsg = validateEditableColumnRename(
      trimmed,
      column.columnIndex,
      column.allColumnNames
    );
    if (validationMsg) {
      setNameError(validationMsg);
      setName(displayName);
      return;
    }
    setNameError(null);
    if (trimmed !== displayName) {
      persistFieldUpdates({ col_name: trimmed });
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLTextAreaElement).blur();
    }
  };

  const handleDescriptionBlur = () => {
    if (isLocked) return;
    const trimmed = description.trim();
    const current = (column.description ?? '').trim();
    if (trimmed !== current) {
      // Use '' (not undefined) when clearing so JSON.stringify includes it in the request body
      persistFieldUpdates({ description: trimmed });
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;
    const value = e.target.value as SpreadsheetColumnType;
    if (!SPREADSHEET_COLUMN_TYPES.includes(value)) return;
    setColType(value);
    persistFieldUpdates({ col_type: value });
  };

  /** Persist field updates to backend; called on blur, type change, AI toggle, preset/mode selection. */
  const persistFieldUpdates = useCallback(
    (fieldUpdates: Partial<Column>) => {
      if (isLocked || !campaignId || isSaving) return;
      setIsSaving(true);
      setJustSaved(false);
      updateColumn(campaignId, column.columnName, fieldUpdates, viewType)
        .then(() => {
          if (fieldUpdates.col_name != null) {
            // non-blocking CRM mapping update
            void renameColumnInCrmMappingIfPresent(
              campaignId,
              viewType,
              column.columnName,
              fieldUpdates.col_name
            ).catch(() => {
              showSnackbar(
                'Column was renamed, but CRM sync mapping could not be updated. Re-save your CRM mapping if needed.',
                'warning'
              );
            });
          }
          setNameError(null);
          const sync: ColumnUpdatedPayload = {
            columnIndex: column.columnIndex
          };
          if (fieldUpdates.col_name != null) {
            sync.columnName = fieldUpdates.col_name;
            sync.previousColumnName = column.columnName;
          }
          if (fieldUpdates.col_type != null)
            sync.columnType = fieldUpdates.col_type;
          if (fieldUpdates.description !== undefined)
            sync.description = fieldUpdates.description;
          if ('definition' in fieldUpdates)
            sync.definition = fieldUpdates.definition;
          onColumnUpdated?.(sync);
          setIsSaving(false);
          setJustSaved(true);
          if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
          savedTimeoutRef.current = setTimeout(() => setJustSaved(false), 1000);
        })
        .catch((e: unknown) => {
          setIsSaving(false);
          setJustSaved(false);
          showSnackbar(
            e instanceof Error ? e.message : 'Failed to update column',
            'error'
          );
        });
    },
    [
      campaignId,
      column.columnName,
      column.columnIndex,
      isLocked,
      isSaving,
      onColumnUpdated,
      showSnackbar,
      viewType
    ]
  );

  const handleAiEnabledChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (isLocked) return;
      if (checked) {
        setEnrichmentMode('freeText');
        setEnrichmentFreeText('');
      }
      setAiEnabled(checked);
      persistFieldUpdates(
        checked
          ? {
              definition: {
                column_definition_type: 'ai_code_generation_instruction',
                value: ''
              }
            }
          : { definition: undefined }
      );
    },
    [isLocked, persistFieldUpdates]
  );

  /** Close the panel (updates are persisted on blur/change events). */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SidebarPanelHeader
        icon={<ViewColumn />}
        title="Column"
        actionButtons={[
          ...(isLocked
            ? [
                <Tooltip
                  key="info"
                  title={LOCKED_COLUMN_TOOLTIP}
                  placement="left"
                  arrow
                >
                  <IconButton
                    size="small"
                    aria-label="First column cannot be edited"
                  >
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              ]
            : []),
          <IconButton
            key="close"
            size="small"
            onClick={handleClose}
            aria-label="Close column editor"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ]}
      />

      {isLocked && (
        <Tooltip title={LOCKED_COLUMN_TOOLTIP} placement="top" arrow>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
          >
            <InfoIcon sx={{ fontSize: 16 }} color="action" />
            <Box
              component="span"
              sx={{ typography: 'caption', color: 'text.secondary' }}
            >
              {LOCKED_COLUMN_TOOLTIP}
            </Box>
          </Box>
        </Tooltip>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="subtitle2">Column settings</Typography>
          {isSaving && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              aria-live="polite"
            >
              <Typography variant="caption">Saving…</Typography>
            </Box>
          )}
          {justSaved && !isSaving && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              aria-live="polite"
            >
              <CheckIcon sx={{ fontSize: 14 }} color="primary" />
              <Typography variant="caption" color="primary">
                Saved
              </Typography>
            </Box>
          )}
        </Box>
        <TextField
          label="Column name"
          value={name}
          onChange={(e) => {
            if (isLocked || isSaving) return;
            setName(e.target.value);
            setNameError(null);
          }}
          onBlur={handleNameBlur}
          onKeyDown={handleNameKeyDown}
          placeholder="Enter column name"
          size="small"
          fullWidth
          disabled={isLocked || isSaving}
          error={Boolean(nameError)}
          helperText={nameError ?? undefined}
          InputProps={isLocked ? { readOnly: true } : undefined}
        />
        <TextField
          select
          label="Type"
          value={colType}
          onChange={handleTypeChange}
          size="small"
          fullWidth
          disabled={isLocked || isSaving}
        >
          {SPREADSHEET_COLUMN_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {SPREADSHEET_COLUMN_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>

        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              AI enrichment
            </Typography>
            <Switch
              checked={aiEnabled}
              onChange={handleAiEnabledChange}
              size="small"
              color="primary"
              disabled={isLocked || isSaving}
              inputProps={{
                'aria-label': 'Toggle AI enrichment for this column'
              }}
            />
          </Box>
          {aiEnabled && (
            <>
              <ToggleButtonGroup
                value={enrichmentMode}
                exclusive
                disabled={isSaving}
                onChange={(_, value) => {
                  if (value == null || isSaving) return;
                  setEnrichmentMode(value);
                  if (value === 'presets') {
                    // Store current free text in memory so it can be restored when switching back to free text
                    freeTextDraftRef.current = enrichmentFreeText.trim();
                    const definitionText =
                      enrichmentPresets.find((p) => p.id === enrichmentPresetId)
                        ?.label ?? '';
                    persistFieldUpdates({
                      definition: {
                        column_definition_type: 'defined_signal',
                        value: definitionText
                      }
                    });
                  } else {
                    // Coming from presets: restore stored draft (user's prior free text) or show empty—never show preset text
                    const wasPreset = definitionType === 'defined_signal';
                    const valueToUse = wasPreset
                      ? (freeTextDraftRef.current ?? '')
                      : enrichmentFreeText.trim();
                    setEnrichmentFreeText(valueToUse);
                    persistFieldUpdates({
                      definition: {
                        column_definition_type:
                          'ai_code_generation_instruction',
                        value: valueToUse
                      }
                    });
                  }
                }}
                size="small"
                fullWidth
                sx={{
                  mb: 1.5,
                  '& .MuiToggleButton-root': {
                    py: 0.75,
                    textTransform: 'none'
                  }
                }}
              >
                <ToggleButton value="presets">Presets</ToggleButton>
                <ToggleButton value="freeText">Free text</ToggleButton>
              </ToggleButtonGroup>
              {enrichmentMode === 'presets' && (
                <Autocomplete
                  options={enrichmentPresets}
                  loading={enrichmentPresetsLoading}
                  disabled={isSaving}
                  getOptionLabel={(option: EnrichmentPreset) => option.label}
                  value={
                    enrichmentPresets.find(
                      (p) => p.id === enrichmentPresetId
                    ) ?? null
                  }
                  onChange={(
                    _,
                    value: { id: string; label: string } | null
                  ) => {
                    if (isSaving) return;
                    const newId = value?.id ?? '';
                    setEnrichmentPresetId(newId);
                    const defValue =
                      enrichmentPresets.find((p) => p.id === newId)?.label ??
                      '';
                    persistFieldUpdates({
                      definition: {
                        column_definition_type: 'defined_signal',
                        value: defValue
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Preset"
                      size="small"
                      fullWidth
                    />
                  )}
                  isOptionEqualToValue={(
                    option: EnrichmentPreset,
                    value: EnrichmentPreset
                  ) => option.id === value.id}
                />
              )}
              {enrichmentMode === 'freeText' && (
                <TextField
                  label="Custom prompt"
                  value={enrichmentFreeText}
                  onChange={(e) =>
                    !isSaving && setEnrichmentFreeText(e.target.value)
                  }
                  onBlur={() => {
                    if (isSaving) return;
                    const defText = enrichmentFreeText.trim();
                    persistFieldUpdates({
                      definition: {
                        column_definition_type:
                          'ai_code_generation_instruction',
                        value: defText
                      }
                    });
                  }}
                  placeholder="Describe how the AI should enrich this column..."
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={5}
                  disabled={isSaving}
                  sx={{
                    '& textarea': {
                      resize: 'vertical',
                      minHeight: 48,
                      overflowY: 'auto !important',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': {
                        width: 6
                      }
                    }
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ColumnEditorPanel;
